import { decodeHTMLEntities } from "@/lib/utils";
import { toPlainText } from "@/lib/markdown";
import { MessageEntity } from "@/indexedDB/types";
import { ChatType } from "@/server/types/chat";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

type ChatLastMessageParserType = {
  message: MessageEntity | null;
  chatType: ChatType;
  description?: string;
};

// Mirrors the orchestrator constant; kept local to avoid a circular import
// between hooks and middleware. Update both if it ever changes.
const MAX_DECRYPTION_ATTEMPTS = 5;

const useChatLastMessageParser = ({
  message,
  chatType,
  description,
}: ChatLastMessageParserType): string => {
  const { t } = useTranslation();
  const [output, setOutput] = useState<string>("");
  useEffect(() => {
    // If there's no last message, return the default prompt.
    if (!message) {
      setOutput(description || "Start the conversation");
      return;
    }

    // Decryption status takes precedence over content rendering: a "failed"
    // row may have empty content text, but we want to show a meaningful
    // status string instead of an empty preview.
    if (message.decryptionStatus === "failed") {
      const attempts = message.decryptionAttempts ?? 0;
      if (attempts >= MAX_DECRYPTION_ATTEMPTS) {
        setOutput(
          t("decryption.unableToDecrypt", "Unable to decrypt — tap to report"),
        );
      } else {
        setOutput(t("decryption.decrypting", "Decrypting…"));
      }
      return;
    }

    const rawText = message.content.text;
    let cleanText = rawText ? toPlainText(rawText) : "";

    cleanText = decodeHTMLEntities(cleanText);

    // An attachment whose metadata failed to decrypt has no decoded
    // media/file/voice, but the crypto layer flags it with
    // `attachmentDecryptionFailed` (see middleware/crypto.ts). Treat it as an
    // attachment for preview purposes so the row shows a meaningful placeholder
    // instead of a blank line. We key off the explicit flag only — not a bare
    // `hasAttachment` with empty content — so unrelated inconsistent-empty
    // states (migrations, pending/lazy rows, future attachment types) are not
    // silently masked. (decryptionStatus is "ok" here — the text portion
    // succeeded — so the failed-status branch above does not apply.)
    const attachmentUndecryptable =
      !cleanText && !!message.content.attachmentDecryptionFailed;

    const lastMessageAsAttachment =
      !cleanText &&
      (message.content.media ||
        message.content.file ||
        attachmentUndecryptable);
    const lastMessageAsVoice = !cleanText && message.content.voice;

    // Determine the msg author label
    const senderLabel = message.isOutgoing ? "You" : message.sender;

    // If the cleaned text is empty and there's an attachment,
    // indicate that a file was sent.
    if (lastMessageAsAttachment) {
      cleanText = t("sentAttachment", "sent an attachment");
    }

    if (lastMessageAsVoice) {
      cleanText = "Voice Message";
    }

    if (chatType === ChatType.GROUP) {
      setOutput(
        lastMessageAsAttachment
          ? `${senderLabel} ${cleanText}`
          : `${senderLabel}: ${cleanText}`,
      );
    } else {
      // For non-group chats, simply return the cleaned text.
      setOutput(
        lastMessageAsAttachment ? `${senderLabel} ${cleanText}` : cleanText,
      );
    }
  }, [chatType, description, message, t]);
  return output;
};

export default useChatLastMessageParser;
